import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Building2, Plus, LogOut, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_7cce28e4-fe0a-4a9e-8874-d3a0b48d9685/artifacts/e1fqiqg9_logo-square-transparent-13ddc4b%20%281%29.png";

const SelectCompany = () => {
  const navigate = useNavigate();
  const { user, logout, isMasterAdmin } = useAuth();
  const { companies, refreshCompanies, selectCompany } = useCompany();
  const [loadingId, setLoadingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    refreshCompanies();
  }, [refreshCompanies]);

  const handleSelect = async (companyId) => {
    setLoadingId(companyId);
    try {
      await selectCompany(companyId);
      navigate('/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to select company');
    } finally {
      setLoadingId(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await api.post('/companies', { name: newName.trim() });
      toast.success('Company created');
      setNewName('');
      setCreating(false);
      refreshCompanies();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create company');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f1a2e] via-[#1a2842] to-[#235894] flex items-center justify-center p-6">
      <div className="w-full max-w-3xl">
        <div className="flex items-center justify-between mb-8 text-white">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Beyond Delta" className="w-12 h-12" />
            <div>
              <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans']">Beyond Delta</h1>
              <p className="text-white/70 text-sm">
                {user?.name} · {isMasterAdmin() ? <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Master Admin</span> : user?.role}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={() => { logout(); navigate('/login'); }}
            className="text-white/80 hover:text-white hover:bg-white/10"
            data-testid="select-company-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>

        <Card className="border-white/10 bg-white/95 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold mb-2 text-slate-900 font-['Plus_Jakarta_Sans']">Choose your workspace</h2>
            <p className="text-slate-500 mb-8">
              Select the company you want to work in. {isMasterAdmin() ? 'As master admin, you can see and manage all workspaces.' : ''}
            </p>

            {companies.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="mb-4">No companies yet.</p>
                {isMasterAdmin() && (
                  <Button onClick={() => setCreating(true)} data-testid="create-first-company-btn">
                    <Plus className="w-4 h-4 mr-2" /> Create your first company
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {companies.map(c => (
                  <button
                    key={c.id}
                    onClick={() => handleSelect(c.id)}
                    disabled={loadingId === c.id || !c.is_active}
                    data-testid={`select-company-${c.id}`}
                    className="group text-left p-5 rounded-xl border-2 border-slate-200 hover:border-[#26B8A7] hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed bg-white"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#235894] to-[#26B8A7] text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
                        {c.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                        {c.description && (
                          <div className="text-xs text-slate-500 mt-1 line-clamp-2">{c.description}</div>
                        )}
                        {!c.is_active && (
                          <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-500">Inactive</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {isMasterAdmin() && companies.length > 0 && !creating && (
              <Button
                variant="outline"
                onClick={() => setCreating(true)}
                className="mt-6"
                data-testid="add-company-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add company
              </Button>
            )}

            {creating && (
              <form onSubmit={handleCreate} className="mt-6 flex gap-2">
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Company name"
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#26B8A7]"
                  data-testid="new-company-name-input"
                  autoFocus
                />
                <Button type="submit" className="bg-[#235894] hover:bg-[#1E4B80]" data-testid="confirm-create-company-btn">Create</Button>
                <Button type="button" variant="ghost" onClick={() => { setCreating(false); setNewName(''); }}>Cancel</Button>
              </form>
            )}

            {isMasterAdmin() && (
              <div className="mt-8 pt-6 border-t border-slate-200">
                <Button
                  variant="outline"
                  onClick={() => navigate('/master-admin')}
                  data-testid="goto-master-admin-btn"
                  className="w-full"
                >
                  <ShieldCheck className="w-4 h-4 mr-2" /> Manage all companies & users (Master Admin)
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SelectCompany;
